/**
 * Zustand store for annotation/issues UI state management.
 *
 * Manages:
 * - Selected annotation for detail view
 * - Filter state for issue lists
 * - Context menu state for data tables
 * - Modal open/close state
 * - Bulk selection state
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {
  Annotation,
  AnnotationFilters,
  AnnotationType,
} from '@/types/annotation'

// =============================================================================
// Types
// =============================================================================

export interface ContextMenuState {
  isOpen: boolean
  x: number
  y: number
  datasetName: string
  recordKey: Record<string, unknown>
  fieldName?: string
  fieldValue?: unknown
  sourceModuleKey?: string
  sourcePageKey?: string
}

export interface CreateAnnotationModalState {
  isOpen: boolean
  type: AnnotationType
  datasetName: string
  recordKey: Record<string, unknown>
  fieldName?: string
  fieldValue?: unknown
  sourceModuleKey?: string
  sourcePageKey?: string
  prefilledContent?: string
  presetCategory?: string
  presetPriority?: string
}

export interface AnnotationDetailState {
  isOpen: boolean
  annotationId: number | null
  annotation: Annotation | null
}

// =============================================================================
// Store State
// =============================================================================

interface AnnotationState {
  // -------------------------------------------------------------------------
  // Filters for issue lists
  // -------------------------------------------------------------------------
  filters: AnnotationFilters
  setFilters: (filters: Partial<AnnotationFilters>) => void
  resetFilters: () => void

  // -------------------------------------------------------------------------
  // Sorting
  // -------------------------------------------------------------------------
  sortBy: string
  sortOrder: 'asc' | 'desc'
  setSorting: (sortBy: string, sortOrder: 'asc' | 'desc') => void

  // -------------------------------------------------------------------------
  // Pagination
  // -------------------------------------------------------------------------
  page: number
  pageSize: number
  setPage: (page: number) => void
  setPageSize: (pageSize: number) => void

  // -------------------------------------------------------------------------
  // Context menu (right-click on table row/cell)
  // -------------------------------------------------------------------------
  contextMenu: ContextMenuState
  openContextMenu: (state: Omit<ContextMenuState, 'isOpen'>) => void
  closeContextMenu: () => void

  // -------------------------------------------------------------------------
  // Create annotation modal
  // -------------------------------------------------------------------------
  createModal: CreateAnnotationModalState
  openCreateModal: (state: Omit<CreateAnnotationModalState, 'isOpen'>) => void
  closeCreateModal: () => void
  setCreateModalType: (type: AnnotationType) => void

  // -------------------------------------------------------------------------
  // Annotation detail panel/modal
  // -------------------------------------------------------------------------
  detailPanel: AnnotationDetailState
  openDetailPanel: (annotationId: number, annotation?: Annotation | null) => void
  closeDetailPanel: () => void
  setDetailAnnotation: (annotation: Annotation | null) => void

  // -------------------------------------------------------------------------
  // Bulk selection (for issue list)
  // -------------------------------------------------------------------------
  selectedIds: Set<number>
  toggleSelected: (id: number) => void
  selectAll: (ids: number[]) => void
  clearSelection: () => void
  isSelected: (id: number) => boolean

  // -------------------------------------------------------------------------
  // Sticky notes page state
  // -------------------------------------------------------------------------
  stickyNotesFilters: {
    datasetName?: string
    searchText?: string
  }
  setStickyNotesFilters: (filters: { datasetName?: string; searchText?: string }) => void

  // -------------------------------------------------------------------------
  // UI preferences
  // -------------------------------------------------------------------------
  showUnreadOnly: boolean
  setShowUnreadOnly: (show: boolean) => void
}

// =============================================================================
// Default Values
// =============================================================================

const defaultFilters: AnnotationFilters = {
  annotationTypes: undefined,
  statuses: undefined,
  categories: undefined,
  priorities: undefined,
  datasetName: undefined,
  assignedToUserId: undefined,
  assignedToRole: undefined,
  createdBy: undefined,
  isRead: undefined,
  sourceModuleKey: undefined,
  sourcePageKey: undefined,
  searchText: undefined,
  createdAfter: undefined,
  createdBefore: undefined,
}

const defaultContextMenu: ContextMenuState = {
  isOpen: false,
  x: 0,
  y: 0,
  datasetName: '',
  recordKey: {},
  fieldName: undefined,
  fieldValue: undefined,
  sourceModuleKey: undefined,
  sourcePageKey: undefined,
}

const defaultCreateModal: CreateAnnotationModalState = {
  isOpen: false,
  type: 'issue',
  datasetName: '',
  recordKey: {},
  fieldName: undefined,
  fieldValue: undefined,
  sourceModuleKey: undefined,
  sourcePageKey: undefined,
  prefilledContent: undefined,
  presetCategory: undefined,
  presetPriority: undefined,
}

const defaultDetailPanel: AnnotationDetailState = {
  isOpen: false,
  annotationId: null,
  annotation: null,
}

// =============================================================================
// Store
// =============================================================================

export const useAnnotationStore = create<AnnotationState>()(
  devtools(
    (set, get) => ({
      // -----------------------------------------------------------------------
      // Filters
      // -----------------------------------------------------------------------
      filters: { ...defaultFilters },
      setFilters: (newFilters) =>
        set(
          (state) => ({
            filters: { ...state.filters, ...newFilters },
            page: 1, // Reset to first page when filters change
          }),
          false,
          'setFilters'
        ),
      resetFilters: () =>
        set(
          { filters: { ...defaultFilters }, page: 1 },
          false,
          'resetFilters'
        ),

      // -----------------------------------------------------------------------
      // Sorting
      // -----------------------------------------------------------------------
      sortBy: 'created_at',
      sortOrder: 'desc',
      setSorting: (sortBy, sortOrder) =>
        set({ sortBy, sortOrder }, false, 'setSorting'),

      // -----------------------------------------------------------------------
      // Pagination
      // -----------------------------------------------------------------------
      page: 1,
      pageSize: 50,
      setPage: (page) => set({ page }, false, 'setPage'),
      setPageSize: (pageSize) =>
        set({ pageSize, page: 1 }, false, 'setPageSize'),

      // -----------------------------------------------------------------------
      // Context menu
      // -----------------------------------------------------------------------
      contextMenu: { ...defaultContextMenu },
      openContextMenu: (state) =>
        set(
          { contextMenu: { ...state, isOpen: true } },
          false,
          'openContextMenu'
        ),
      closeContextMenu: () =>
        set(
          { contextMenu: { ...defaultContextMenu } },
          false,
          'closeContextMenu'
        ),

      // -----------------------------------------------------------------------
      // Create modal
      // -----------------------------------------------------------------------
      createModal: { ...defaultCreateModal },
      openCreateModal: (state) =>
        set(
          { createModal: { ...state, isOpen: true } },
          false,
          'openCreateModal'
        ),
      closeCreateModal: () =>
        set(
          { createModal: { ...defaultCreateModal } },
          false,
          'closeCreateModal'
        ),
      setCreateModalType: (type) =>
        set(
          (state) => ({
            createModal: { ...state.createModal, type },
          }),
          false,
          'setCreateModalType'
        ),

      // -----------------------------------------------------------------------
      // Detail panel
      // -----------------------------------------------------------------------
      detailPanel: { ...defaultDetailPanel },
      openDetailPanel: (annotationId, annotation = null) =>
        set(
          {
            detailPanel: {
              isOpen: true,
              annotationId,
              annotation,
            },
          },
          false,
          'openDetailPanel'
        ),
      closeDetailPanel: () =>
        set(
          { detailPanel: { ...defaultDetailPanel } },
          false,
          'closeDetailPanel'
        ),
      setDetailAnnotation: (annotation) =>
        set(
          (state) => ({
            detailPanel: { ...state.detailPanel, annotation },
          }),
          false,
          'setDetailAnnotation'
        ),

      // -----------------------------------------------------------------------
      // Bulk selection
      // -----------------------------------------------------------------------
      selectedIds: new Set<number>(),
      toggleSelected: (id) =>
        set(
          (state) => {
            const newSet = new Set(state.selectedIds)
            if (newSet.has(id)) {
              newSet.delete(id)
            } else {
              newSet.add(id)
            }
            return { selectedIds: newSet }
          },
          false,
          'toggleSelected'
        ),
      selectAll: (ids) =>
        set({ selectedIds: new Set(ids) }, false, 'selectAll'),
      clearSelection: () =>
        set({ selectedIds: new Set<number>() }, false, 'clearSelection'),
      isSelected: (id) => get().selectedIds.has(id),

      // -----------------------------------------------------------------------
      // Sticky notes filters
      // -----------------------------------------------------------------------
      stickyNotesFilters: {},
      setStickyNotesFilters: (filters) =>
        set({ stickyNotesFilters: filters }, false, 'setStickyNotesFilters'),

      // -----------------------------------------------------------------------
      // UI preferences
      // -----------------------------------------------------------------------
      showUnreadOnly: false,
      setShowUnreadOnly: (show) =>
        set({ showUnreadOnly: show }, false, 'setShowUnreadOnly'),
    }),
    { name: 'annotation-store' }
  )
)

// =============================================================================
// Selectors
// =============================================================================

/**
 * Get selected annotation IDs as an array.
 */
export const useSelectedAnnotationIds = () =>
  useAnnotationStore((state) => Array.from(state.selectedIds))

/**
 * Get selection count.
 */
export const useSelectionCount = () =>
  useAnnotationStore((state) => state.selectedIds.size)

/**
 * Check if any annotations are selected.
 */
export const useHasSelection = () =>
  useAnnotationStore((state) => state.selectedIds.size > 0)

/**
 * Get current filters with defaults merged.
 */
export const useAnnotationFilters = () =>
  useAnnotationStore((state) => state.filters)

/**
 * Check if any filters are active.
 */
export const useHasActiveFilters = () =>
  useAnnotationStore((state) => {
    const f = state.filters
    return !!(
      f.annotationTypes?.length ||
      f.statuses?.length ||
      f.categories?.length ||
      f.priorities?.length ||
      f.datasetName ||
      f.assignedToUserId ||
      f.assignedToRole ||
      f.createdBy ||
      f.isRead !== undefined ||
      f.sourceModuleKey ||
      f.sourcePageKey ||
      f.searchText ||
      f.createdAfter ||
      f.createdBefore
    )
  })
